import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export const ALL_ORDERS_KEY = "all_orders";

/**
 * Orders `{ [statusKey]: count }` entries for display: `all_orders` first when present,
 * everything else in insertion order. Returns [] for null/undefined data.
 */
export const orderEntries = (data) => {
  const entries = Object.entries(data || {});
  const allOrders = entries.find(([key]) => key === ALL_ORDERS_KEY);
  const rest = entries.filter(([key]) => key !== ALL_ORDERS_KEY);
  return allOrders ? [allOrders, ...rest] : rest;
};

/**
 * URL-param backed status selector shared by the desktop `OrderFilter` list and the
 * mobile `StatusChipStrip`. Both surfaces must agree on these semantics:
 *
 * - the selected key is `?<paramName>=`, else `defaultSelect`, else `all_orders`
 * - selecting `all_orders` DELETES the param (it is never written as a literal value)
 * - selecting anything else SETS the param to that key
 * - either way the popup filters (`filterData`) are cleared and `page` resets to 1
 */
export function useStatusParamFilter({ defaultSelect, paramName = "filter_status" } = {}) {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const selected = urlSearchParams.get(paramName) || defaultSelect || ALL_ORDERS_KEY;

  const select = useCallback(
    (id) => {
      setUrlSearchParams((prev) => {
        if (id === ALL_ORDERS_KEY) {
          prev.delete(paramName);
        } else {
          prev.set(paramName, id);
        }
        prev.delete("filterData");
        prev.set("page", 1);
        return prev;
      });
    },
    [paramName, setUrlSearchParams],
  );

  return { selected, select };
}
